import { FileTypeEnum, UserTerminalLogoStatusResponseModel, fileApis, uploadApis, userApis } from "@payping/api";
import { Add } from "@payping/icons";
import { getUserBusinessinfo, getUserTerminalList } from "@payping/redux/actions";
import { DashboardRoutes } from "@/router/constant";
import {
  Bank,
  Button,
  CardList,
  Col,
  Descriptions,
  Empty,
  Flex,
  Image,
  Radio,
  Row,
  Tag,
  Typography,
  Upload,
  useMessagedAsync,
  useMessagedAsyncFn,
} from "@payping/web-components";
import { UploadChangeParam, UploadFile, UploadRequestOption } from "@payping/web-components/components/Upload/types";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { useAppDispatch, useAppSelector } from "@/utils/redux";
import { PaymentGateway, useIpgTypeMap } from "@/pages/Settings/Terminal/data";
import { DisplayModeEnum, ListCardProps } from "@payping/web-components/components/List/types";
import { buildUrl } from "@payping/utilities-shared/url";
import styles from "./terminal.module.css";
import { URLS } from "@payping/utilities-shared/consts";

const Terminal: React.FC = () => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [{ terminalLogoVerification, terminalLogo }, setTerminalStatus] = useState<UserTerminalLogoStatusResponseModel>(
    {
      terminalLogo: "",
      terminalLogoVerification: true,
    },
  );
  const { t } = useAppTranslation();
  const [selectedImage, setSelectedImage] = useState<PaymentGateway>(PaymentGateway.Sadad);
  // const businessName = useAppSelector((state) => state.user.businessInfo?.businessName);
  const isDefaultPlan = useAppSelector((state) => state.user.panelInfo?.currentPlan?.isDefaultPlan);
  const terminalList = useAppSelector((state) => state.reports.terminalList);
  const dispatch = useAppDispatch();
  const ipgTypeMap = useIpgTypeMap();
  const navigate = useNavigate();

  useEffect(() => {
    if (terminalLogo) {
      setFileList([
        {
          name: "image",
          size: 0,
          type: "image/*",
          uid: "0",
          url: terminalLogo,
        },
      ]);
    }
  }, [terminalLogo]);

  const ImagePaymentGateway: Record<PaymentGateway, string> = {
    [PaymentGateway.Sadad]: buildUrl(URLS.STORAGE_URL, "img/document/terminals/Sadad_Desktop.png").toString(),
    [PaymentGateway.Mellat]: buildUrl(URLS.STORAGE_URL, "img/document/terminals/Behpardakh_Desktop.png").toString(),
    [PaymentGateway.Pep]: buildUrl(URLS.STORAGE_URL, "img/document/terminals/Pasargad_Desktop.png").toString(),
    [PaymentGateway.Saman]: buildUrl(URLS.STORAGE_URL, "img/document/terminals/Sep_Desktop.png").toString(),
    [PaymentGateway.Mabna]: buildUrl(URLS.STORAGE_URL, "img/document/terminals/Sepehr_Desktop.png").toString(),
  };

  const { loading: loadingTerminalList } = useMessagedAsync(() => dispatch(getUserTerminalList()), [], {
    success: false,
    useProgress: true,
  });

  const data: ListCardProps["dataSource"] = terminalList.map((terminal) => ({
    key: terminal.terminalCode || "",
    image: { type: "icon", content: <Bank.Icon showTooltip ipg={terminal.ipgType} width={58} /> },
    title: {
      primary: <Typography.Title level={5}>{ipgTypeMap(terminal.ipgType)}</Typography.Title>,
      tag: <Tag>{terminal.isBusiness ? t("common.legal") : t("common.natural")}</Tag>,
    },
    // TODO replace with proper card
    descriptions: (
      <Descriptions>
        <Descriptions.Item
          className={"!tw-p-0"}
          span={1}
          label={<Typography.Text strong>{t("terminals.table.acceptorCode")}</Typography.Text>}
        >
          <Typography.Text type={"secondary"}>{terminal.acceptorCode}</Typography.Text>
        </Descriptions.Item>
        <Descriptions.Item
          className={"!tw-p-0"}
          span={1}
          label={<Typography.Text strong>{t("terminals.table.terminalId")}</Typography.Text>}
        >
          <Typography.Text type={"secondary"}>{terminal.terminalCode}</Typography.Text>
        </Descriptions.Item>
      </Descriptions>
    ),
  }));

  const { loading } = useMessagedAsync(
    async () => {
      await dispatch(getUserBusinessinfo());
      const status = await userApis.terminalLogoStatusWithCancellationTokenGet();
      setTerminalStatus(status);
    },
    [],
    { useProgress: true },
  );

  const [{ loading: loadingUpload }, handleUpload] = useMessagedAsyncFn(async (options: UploadRequestOption) => {
    const { file, onSuccess, onError } = options;
    uploadApis
      .miscWithFilePost(file as Blob)
      .then(async (fileName) => {
        await fileApis.addFileWithRequestModelWithCancellationTokenPost({
          fileName,
          type: FileTypeEnum.TerminalLogo,
        });
        onSuccess?.(fileName);
      })
      .catch(onError);
    return {
      abort() {
        // eslint-disable-next-line no-console
      },
    };
  }, []);

  return (
    <Row gutter={[0, 32]}>
      {/* < تایتل صفه: پایانه های فروش/> */}
      <Col span={24}>
        <Typography.Title className="!tw-m-0 tw-font-medium" level={4}>
          {t("settings.terminal.terminals")}
        </Typography.Title>
      </Col>
      {/* < تایتل صفه: پایانه های فروش /> */}

      {/* < لیست ترمینال ها > */}
      <Col span={24}>
        <CardList
          loading={!data.length ? loadingTerminalList : false}
          dataSource={data}
          grid={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 1, xxl: 1, column: 1 }}
          mode={DisplayModeEnum.List}
          className={styles.payPingListCardCustom}
          locale={{
            emptyText: (
              <Empty
                description={
                  <Typography.Text className={"color--black"}>{t("terminals.emptyTerminal")}</Typography.Text>
                }
              />
            ),
          }}
        />
      </Col>
      {/* < لیست ترمینال ها /> */}

      {/* < لوگو اختصاصی کسب و کار شما برای هر ترمینال > */}
      <Col span={24}>
        <Flex vertical gap={"middle"}>
          <div className={"tw-flex tw-justify-between"}>
            <Typography.Title className={"!tw-m-0"} level={5}>
              {t("settings.terminal.dedicatedLogo")}
            </Typography.Title>
            {isDefaultPlan ? (
              <Button type={"primary"} onClick={() => navigate(DashboardRoutes.user.upgrade.url)}>
                {t("mySales.withdrawTab.upgradePanel")}
              </Button>
            ) : null}
          </div>
          <Typography.Text type={"secondary"}>
            {isDefaultPlan
              ? t("settings.terminal.dedicatedLogoContentDefaultPlan")
              : t("settings.terminal.dedicatedLogoContent")}
          </Typography.Text>
          {!isDefaultPlan ? (
            <Upload
              disabled={loading || terminalLogoVerification || loadingUpload}
              listType={"picture-card"}
              customRequest={(options) => handleUpload(options)}
              onChange={(info: UploadChangeParam): void => setFileList(info.fileList)}
              fileList={fileList}
              showUploadList={{ showPreviewIcon: true, showRemoveIcon: true }}
              allowedExtensions={["jpg", "jpeg", "png"]}
            >
              {!fileList.length && (
                <div>
                  <Add width={"20px"} />
                  <div className={"ant-upload-text"}>{t("settings.terminal.image")}</div>
                </div>
              )}
            </Upload>
          ) : null}
        </Flex>
      </Col>
      {/* < لوگو اختصاصی کسب و کار شما برای هر ترمینال /> */}

      {/* < پیش نمایش بر روی ترمینال > */}
      <Flex vertical gap={"large"}>
        <Typography.Text>{t("settings.terminal.previewOnTheTerminal")}</Typography.Text>
        <Radio.Group onChange={(e) => setSelectedImage(e.target.value)} value={selectedImage}>
          <Radio.Button value={PaymentGateway.Sadad}>{t("common.gateway.Sadad")}</Radio.Button>
          <Radio.Button value={PaymentGateway.Mellat}>{t("common.gateway.Mellat")}</Radio.Button>
          <Radio.Button value={PaymentGateway.Pep}>{t("common.gateway.Pep")}</Radio.Button>
          <Radio.Button value={PaymentGateway.Saman}>{t("common.gateway.SepOnlinePG")}</Radio.Button>
          <Radio.Button value={PaymentGateway.Mabna}>{t("common.gateway.Mabna")}</Radio.Button>
        </Radio.Group>
        <Image preview={false} width={"60%"} src={ImagePaymentGateway[selectedImage]} />
      </Flex>
      {/* < پیش نمایش بر روی ترمینال /> */}
    </Row>
  );
};

export default Terminal;
